import type { LinkMode, SiteDetail, SiteState, StepCode, StepNo, StepStatus } from '../schemas/sites.js';
import { cityLabel, tzExample } from './cities.js';

/**
 * Тексты шагов подключения площадки (007, FR-010…FR-027, research R19): заголовки, строки состояния, подсказки по
 * каждому коду и действия-ссылки. Источник — прототип `prototypes/site-onboarding.html` (функции `summary` и `body`)
 * и `site-onboarding-design.md` (раздел 4), слово в слово. Где прототип и спецификация расходятся, взята
 * спецификация (FR-024 — одно время выгрузки, FR-025 — без похожих пар). Тексты — ASD-STE100: короткие предложения,
 * нейтральный род, названия кнопок и полей — как в SmartPSS, Windows и WireGuard (FR-072). Коды — отдельными полями,
 * в тексты не попадают. Детали кодов — как их пишет `api/src/modules/sites/checks.ts`. Функции чистые.
 */

// ── Шаги ──

export const STEP_TITLES: Readonly<Record<StepNo, string>> = {
  1: 'Площадка',
  2: 'Компьютер со SmartPSS',
  3: 'Способ связи',
  4: 'Связь SmartPSS с сервисом',
  5: 'Терминалы',
  6: 'Контрольный проход',
  7: 'Выгрузка по расписанию',
  8: 'Люди площадки',
  9: 'Наблюдение',
  10: 'Запуск',
};

/** Шаги «по желанию»: у заголовка пометка `COMMON.optional`. */
export const OPTIONAL_STEPS: readonly number[] = [6, 8];

/** Заголовок шага: «Шаг 4. Связь SmartPSS с сервисом». */
export function stepHeading(n: number): string {
  return `Шаг ${n}. ${STEP_TITLES[n as StepNo] ?? ''}`;
}

/** Порядок шагов (FR-011): после каких шагов открывается шаг. */
export const STEP_AFTER: Readonly<Record<StepNo, readonly StepNo[]>> = {
  1: [],
  2: [1],
  3: [1],
  4: [3],
  5: [4],
  6: [5],
  7: [1],
  8: [1],
  9: [6, 7],
  10: [1, 2, 3, 4, 5, 6, 7, 9],
};

/** На каких шагах бывает код (research R19). */
export const CODE_STEPS: Readonly<Record<StepCode, readonly StepNo[]>> = {
  PREPARING: [1],
  READY: [1, 2],
  ANSWERS_TODO: [2],
  LINK_DIRECT: [3],
  VPN_WAIT: [3],
  VPN_OK: [3],
  LOGIN_WAIT: [4],
  LOGGED_IN: [4],
  LINK_OK: [4],
  TERMINALS_WAIT: [5],
  TERMINALS_CHOOSE: [5],
  TERMINALS_OK: [5],
  CTL_WAIT: [6],
  CTL_OK: [6],
  CTL_SKIPPED: [6],
  EXTRACT_TODO: [7],
  EXTRACT_WAIT: [7],
  EXTRACT_OK: [7],
  EXTRACT_MANUAL: [7],
  PEOPLE_NONE: [8],
  PEOPLE_OK: [8],
  OBSERVING: [9],
  OBSERVED: [9],
  LAUNCH_READY: [10],
  LAUNCHED: [10],
  LOCKED: [2, 3, 4, 5, 6, 7, 8, 9, 10],
  PREPARE_FAILED: [1],
  VERSION_UNKNOWN: [2],
  RETENTION_SHORT: [2],
  PC_NOT_24H: [2],
  VPN_SILENT: [3],
  VPN_BLOCKED: [3],
  LINK_FAILED: [3],
  PASS_MISMATCH: [4],
  USER_NAME: [4],
  DB_NAME: [4],
  NO_ATTEMPTS: [4],
  NO_TABLE: [4],
  NEW_ADDRESS: [4],
  TERMINALS_MISSING: [5],
  NO_DIRECTION: [5],
  DIRECTION_SWAPPED: [5],
  CLOCK_SKEW: [5],
  CTL_NOT_FOUND: [6],
  CTL_WRONG_TERMINAL: [6],
  CTL_DELAY: [6],
  CTL_CLOCK: [6],
  EXTRACT_MISSED: [7],
  OBS_DELETES: [9],
  OBS_CODES: [9],
};

// ── Действия-ссылки подсказок ──

export const ACTION_KINDS = [
  'copyPassword',
  'copyValue',
  'recheck',
  'allowAddress',
  'gotoStep',
  'switchToDirect',
  'openAmnezia',
  'downloadAmneziaConfig',
  'confirmVpnActive',
  'retryPrepare',
  'restartControl',
  'acceptRisk',
] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];

/**
 * Действие подсказки — ссылка с коротким глаголом (FR-072, `Callout` с действием); главное действие подсказки «нужно
 * исправить» — кнопка (`hintButton`). Что делать, решает интерфейс по `kind`:
 * - `copyPassword` — пароль из `GET …/secret`; `copyValue` — значение `value`;
 * - `recheck` — `POST …/recheck`; `allowAddress` — `POST …/allow-address` с `address`;
 * - `gotoStep` — открыть шаг `step`; `switchToDirect` — `PUT …/link` с `direct`;
 * - `openAmnezia` — внешняя ссылка `href`; `downloadAmneziaConfig` — файл для закрытых сетей;
 * - `confirmVpnActive` — ответ шага 3 «статус «Активен»» (`PUT …/answers`, шаг 3);
 * - `retryPrepare` — повторить подготовку места; `restartControl` — заново нажатия шага 6;
 * - `acceptRisk` — окно «Продолжить с риском» (`POST …/risk`).
 */
export type StepAction =
  | { kind: 'copyValue'; label: string; value: string }
  | { kind: 'allowAddress'; label: string; address: string }
  | { kind: 'gotoStep'; label: string; step: number }
  | { kind: 'openAmnezia'; label: string; href: string }
  | {
      kind: Exclude<ActionKind, 'copyValue' | 'allowAddress' | 'gotoStep' | 'openAmnezia'>;
      label: string;
    };

/** Внешние ссылки шага 3: официальные страницы программ (FR-019). */
export const LINKS = {
  wireguard: 'https://www.wireguard.com/install/',
  amnezia: 'https://github.com/amnezia-vpn/amneziawg-windows-client/releases',
} as const;

export const ACTIONS = {
  copyPassword: (): StepAction => ({ kind: 'copyPassword', label: 'Скопировать пароль' }),
  copyDbName: (value: string): StepAction => ({ kind: 'copyValue', label: 'Скопировать имя базы', value }),
  copyUser: (value: string): StepAction => ({
    kind: 'copyValue',
    label: 'Скопировать имя пользователя',
    value,
  }),
  recheck: (): StepAction => ({ kind: 'recheck', label: 'Проверить снова' }),
  allowAddress: (address: string): StepAction => ({
    kind: 'allowAddress',
    label: `Разрешить адрес ${address}`,
    address,
  }),
  gotoStep: (step: number): StepAction => ({ kind: 'gotoStep', label: `Открыть шаг ${step}`, step }),
  switchToDirect: (): StepAction => ({ kind: 'switchToDirect', label: 'Подключить напрямую' }),
  openAmnezia: (): StepAction => ({ kind: 'openAmnezia', label: 'Программа AmneziaWG', href: LINKS.amnezia }),
  downloadAmneziaConfig: (): StepAction => ({
    kind: 'downloadAmneziaConfig',
    label: 'Скачать файл для закрытых сетей',
  }),
  confirmVpnActive: (): StepAction => ({
    kind: 'confirmVpnActive',
    label: 'Статус «Активен», а сигнала нет',
  }),
  retryPrepare: (): StepAction => ({ kind: 'retryPrepare', label: 'Повторить' }),
  restartControl: (): StepAction => ({ kind: 'restartControl', label: 'Начать заново' }),
  acceptRisk: (): StepAction => ({ kind: 'acceptRisk', label: 'Продолжить с риском' }),
} as const;

/**
 * Действия, которые исправляют состояние шага здесь же. Копирование значения, переход к шагу, внешняя страница и риск
 * ничего не исправляют — они остаются ссылками.
 */
export const FIX_ACTIONS: readonly ActionKind[] = [
  'recheck',
  'allowAddress',
  'retryPrepare',
  'restartControl',
  'switchToDirect',
  'downloadAmneziaConfig',
];

/**
 * Главное действие подсказки «нужно исправить» — первое исправляющее действие; интерфейс показывает его кнопкой, а не
 * ссылкой (дизайн-система Steps; 21.10.2026 ссылку «Разрешить адрес» в красной подсказке не нашли). Нет — `null`.
 */
export function hintButton(h: StepHint): StepAction | null {
  if (h.tone !== 'bad') return null;
  return h.actions.find((a) => FIX_ACTIONS.includes(a.kind)) ?? null;
}

// ── Подсказки и вид шага ──

/** Тон подсказки (`Callout`): `wait` — как `info`, со значком часов. */
export type HintTone = 'info' | 'wait' | 'ok' | 'warn' | 'bad';

/** Подсказка шага: заголовок жирным (если есть), текст и действия-ссылки; главное действие «нужно исправить» — кнопкой. */
export interface StepHint {
  readonly tone: HintTone;
  readonly title?: string;
  readonly text?: string;
  readonly actions: readonly StepAction[];
}

/** Тон строки состояния — по статусу шага. */
export type SummaryTone = 'plain' | 'ok' | 'warn' | 'bad';
const SUMMARY_TONE: Readonly<Record<StepStatus, SummaryTone>> = {
  todo: 'plain',
  current: 'plain',
  wait: 'plain',
  ok: 'ok',
  warn: 'warn',
  bad: 'bad',
};

/** Площадка для текстов: значения для SmartPSS, способ связи, пояс. `siteTextContext` собирает его из `SiteDetail`. */
export interface SiteTextContext {
  readonly code: string;
  readonly name: string;
  readonly city: string;
  readonly tzOffset: string;
  readonly linkMode: LinkMode;
  /** IP сервера для SmartPSS: внешний адрес (напрямую) или адрес сервера в VPN. */
  readonly serverIp: string;
  readonly port?: number;
  readonly dbName: string;
  readonly smartpssUser: string;
  /** Есть сервер AmneziaWG (research R6): тогда шаг 3 предлагает его при закрытой сети. */
  readonly amnezia?: boolean;
  /** Сколько шагов готово — для причины под «Подключить к табелям». */
  readonly doneCount?: number;
  readonly launchedAt?: string | null;
}

/** Шаг на входе: как `SiteStep` из ответа сервера. */
export interface StepInput {
  readonly step: number;
  readonly status: StepStatus;
  readonly code: StepCode;
  readonly details?: Readonly<Record<string, unknown>> | null;
  readonly risk?: { readonly reason: string; readonly by?: number; readonly at?: string } | null;
}

/** Что показать у шага: заголовок, строка состояния, причина закрытого шага, подсказки. */
export interface StepView {
  readonly step: number;
  readonly title: string;
  readonly heading: string;
  readonly optional: boolean;
  readonly tone: SummaryTone;
  readonly summary: string;
  /** Закрытый шаг: почему кнопки недоступны (FR-011); у шага 10 — причина под кнопкой (FR-027). */
  readonly note?: string;
  readonly hints: readonly StepHint[];
}

/** Готовый шаг (R19): `ok` или `warn` с принятым риском. */
export function stepReady(s: Pick<StepInput, 'status' | 'risk'>): boolean {
  return s.status === 'ok' || (s.status === 'warn' && !!s.risk);
}

export function siteTextContext(d: SiteDetail): SiteTextContext {
  return {
    code: d.site.code,
    name: d.site.name,
    city: d.site.city,
    tzOffset: d.site.tzOffset,
    linkMode: d.site.linkMode,
    serverIp: d.smartpss.serverIp,
    port: d.smartpss.port,
    dbName: d.smartpss.dbName,
    smartpssUser: d.smartpss.user,
    amnezia: d.vpn?.amnezia ?? false,
    doneCount: d.steps.filter(stepReady).length,
    launchedAt: d.site.launchedAt,
  };
}

/** Вид шага по коду, статусу и деталям. При принятом риске «Продолжить с риском» нет, причина видна. */
export function stepView(s: StepInput, site: SiteTextContext): StepView {
  const r = CODE_TEXTS[s.code]({ step: s.step, status: s.status, d: s.details ?? {}, site });
  let hints = r.hints ?? [];
  if (s.risk && s.status === 'warn') {
    hints = [
      ...hints.map((h) => ({ ...h, actions: h.actions.filter((a) => a.kind !== 'acceptRisk') })),
      hint('info', undefined, RISK_TEXTS.accepted(s.risk.reason)),
    ];
  }
  return {
    step: s.step,
    title: STEP_TITLES[s.step as StepNo] ?? '',
    heading: stepHeading(s.step),
    optional: OPTIONAL_STEPS.includes(s.step),
    tone: SUMMARY_TONE[s.status],
    summary: r.summary,
    ...(r.note ? { note: r.note } : {}),
    hints,
  };
}

// ── Числа, время, перечни ──

/** Форма слова по числу: `plural(5, ['день', 'дня', 'дней'])` → «дней». */
export function plural(n: number, [one, few, many]: readonly [string, string, string]): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b === 1) return one;
  if (b > 1 && b < 5) return few;
  return many;
}
const DAYS = ['день', 'дня', 'дней'] as const;
const PASSES = ['отметка', 'отметки', 'отметок'] as const;
const TERMINALS = ['терминал', 'терминала', 'терминалов'] as const;
const MINUTES_ACC = ['минуту', 'минуты', 'минут'] as const;
const PEOPLE = ['человек', 'человека', 'человек'] as const;
const ITEMS = ['пункт', 'пункта', 'пунктов'] as const;

/** Длительность: «4 с», «1 мин 40 с», «2 мин», «1 ч 5 мин». */
export function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s} с`;
  if (s < 3600) return s % 60 ? `${Math.floor(s / 60)} мин ${s % 60} с` : `${s / 60} мин`;
  const m = Math.floor((s % 3600) / 60);
  return m ? `${Math.floor(s / 3600)} ч ${m} мин` : `${Math.floor(s / 3600)} ч`;
}

function offsetMs(tz: string): number {
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(tz);
  if (!m) return 0;
  const v = (Number(m[2]) * 60 + Number(m[3])) * 60_000;
  return m[1] === '-' ? -v : v;
}
function local(v: string, tz: string): string | null {
  const t = Date.parse(v);
  return Number.isFinite(t) ? new Date(t + offsetMs(tz)).toISOString() : null;
}
/** Время «ЧЧ:ММ» по поясу площадки; «ЧЧ:ММ» на входе — как есть. */
export function localTime(v: string, tz: string): string {
  if (/^\d{2}:\d{2}$/.test(v)) return v;
  return local(v, tz)?.slice(11, 16) ?? v;
}
/** Дата «ДД.ММ.ГГГГ» по поясу площадки. */
export function localDate(v: string, tz: string): string {
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : local(v, tz);
  if (!iso) return v;
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

/** Перечень: «6 и 7», «1–7 и 9», «2, 4–7 и 9». */
export function stepsPhrase(list: readonly number[]): string {
  return joinAnd(stepParts(list));
}
/** Номера шагов группами: `[1, 2, 3, 5]` → `['1–3', '5']`. */
function stepParts(list: readonly number[]): string[] {
  const ns = [...new Set(list)].sort((a, b) => a - b);
  const parts: string[] = [];
  for (let i = 0; i < ns.length; ) {
    let j = i;
    while (j + 1 < ns.length && ns[j + 1] === (ns[j] as number) + 1) j++;
    if (j - i >= 2) parts.push(`${ns[i]}–${ns[j]}`);
    else for (let k = i; k <= j; k++) parts.push(String(ns[k]));
    i = j + 1;
  }
  return parts;
}
/** «шага 3» или «шагов 6 и 7». */
function afterSteps(list: readonly number[]): string {
  const p = stepsPhrase(list);
  return /^\d+$/.test(p) ? `шага ${p}` : `шагов ${p}`;
}
function joinAnd(xs: readonly string[]): string {
  return xs.length < 2 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} и ${xs.at(-1)}`;
}

// ── Чтение деталей ──

type D = Readonly<Record<string, unknown>>;
const isObj = (v: unknown): v is D => !!v && typeof v === 'object' && !Array.isArray(v);
const num = (d: D, k: string): number | null => {
  const v = d[k];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};
const str = (d: D, k: string): string | null => {
  const v = d[k];
  return typeof v === 'string' && v !== '' ? v : null;
};
const objs = (d: D, k: string): D[] => {
  const v = d[k];
  return Array.isArray(v) ? v.filter(isObj) : [];
};
const nums = (d: D, k: string): number[] => {
  const v = d[k];
  return Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : [];
};

function hint(tone: HintTone, title?: string, text?: string, actions: readonly StepAction[] = []): StepHint {
  return { tone, ...(title ? { title } : {}), ...(text ? { text } : {}), actions };
}

// ── Тексты кодов ──

interface CodeContext {
  readonly step: number;
  readonly status: StepStatus;
  readonly d: D;
  readonly site: SiteTextContext;
}
interface CodeResult {
  readonly summary: string;
  readonly hints?: readonly StepHint[];
  readonly note?: string;
}
export type CodeText = (c: CodeContext) => CodeResult;

const tunnel = (site: SiteTextContext) => `efes-${site.code}`;
const port = (site: SiteTextContext) => site.port ?? 3306;
const vpn = (site: SiteTextContext) => site.linkMode === 'vpn';

// Шаг 2: замечания по ответам (FR-018).
type PcProblem = 'VERSION_UNKNOWN' | 'RETENTION_SHORT' | 'PC_NOT_24H';
const PC_PROBLEMS: Readonly<Record<PcProblem, (d: D) => string>> = {
  VERSION_UNKNOWN: () => 'Эту версию мы ещё не проверяли. Можно продолжать: шаг 4 покажет, работает ли она.',
  RETENTION_SHORT: (d) => {
    const k = num(d, 'keepDays');
    const v = k === null ? 'не указан' : `${k} ${plural(k, DAYS)}`;
    return `Срок хранения — ${v}. Поставьте 62 дня или больше. Иначе пропуски нельзя будет восстановить.`;
  },
  PC_NOT_24H: () =>
    'Отметки будут приходить, когда компьютер включат. Табель за день может обновиться позже.',
};
const pcWarn =
  (code: PcProblem): CodeText =>
  ({ d }) => {
    const listed = (Array.isArray(d.problems) ? d.problems : []).filter(
      (p): p is PcProblem => typeof p === 'string' && p in PC_PROBLEMS,
    );
    const list = listed.length ? listed : [code];
    const hints = list.map((p, i) =>
      hint('warn', undefined, PC_PROBLEMS[p](d), i === list.length - 1 ? [ACTIONS.acceptRisk()] : []),
    );
    return { summary: `Есть замечания: ${list.length}`, hints };
  };
function pcSummary(d: D): string {
  const v = str(d, 'version');
  const t = num(d, 'terminals');
  const k = num(d, 'keepDays');
  const parts = [
    v ? `SmartPSS ${v}` : null,
    t === null ? null : `терминалов ${t}`,
    k === null ? null : `хранение ${k} ${plural(k, DAYS)}`,
  ].filter((x): x is string => !!x);
  return parts.length ? parts.join(' · ') : 'Всё в порядке';
}

// Шаг 5: терминалы (FR-022).
type TerminalProblemCode = 'NO_DIRECTION' | 'DIRECTION_SWAPPED' | 'CLOCK_SKEW';
const TERMINAL_PROBLEMS: Readonly<Record<TerminalProblemCode, (t: D) => string>> = {
  NO_DIRECTION: () =>
    'SmartPSS передаёт отметки без направления. Выберите направление каждого терминала и нажмите «Подтвердить направления».',
  DIRECTION_SWAPPED: (t) =>
    `Похоже, направление перепутано: терминал отмечает ${t.chosen === 'OUT' ? 'приход' : 'уход'}.`,
  // Меньше нуля — время отметки позже прихода в копию: часы терминала спешат (R19, `source_ts_ms − attendance_ms`).
  CLOCK_SKEW: (t) => {
    const m = num(t, 'skewMinutes') ?? 0;
    const a = Math.max(1, Math.abs(m));
    return `Часы терминала ${m > 0 ? 'отстают' : 'спешат'} на ${a} ${plural(a, MINUTES_ACC)}. Включите синхронизацию времени в настройках устройства в SmartPSS.`;
  },
};
/** Текст ошибки терминала для строки таблицы шага 5. */
export function terminalProblemText(problem: TerminalProblemCode, terminal: D = {}): string {
  return TERMINAL_PROBLEMS[problem](terminal);
}
const isTerminalProblem = (v: unknown): v is TerminalProblemCode =>
  typeof v === 'string' && v in TERMINAL_PROBLEMS;

function terminalHints(code: StepCode, d: D): StepHint[] {
  const out = objs(d, 'terminals')
    .filter((t) => isTerminalProblem(t.problem))
    .map((t) =>
      hint('bad', `${str(t, 'ip') ?? ''}.`, TERMINAL_PROBLEMS[t.problem as TerminalProblemCode](t)),
    );
  const ip = str(d, 'ip');
  if (out.length === 0 && isTerminalProblem(code) && ip)
    out.push(hint('bad', `${ip}.`, TERMINAL_PROBLEMS[code](d)));
  return out;
}
/** Совет шага 5: имя терминала в SmartPSS равно адресу (FR-022). */
function nameTip(d: D): StepHint[] {
  const same = objs(d, 'terminals').some(
    (t) => !str(t, 'deviceName') || str(t, 'deviceName') === str(t, 'ip'),
  );
  return same ? [hint('info', STEP_BODY[5].nameTip.title, STEP_BODY[5].nameTip.text)] : [];
}
function missingTerminals(d: D): StepHint {
  const seen = num(d, 'seen') ?? 0;
  const expected = num(d, 'expected') ?? seen;
  const k = Math.max(1, expected - seen);
  const text =
    k === 1
      ? 'Один терминал ещё не прислал ни одной отметки. Пройдите мимо него.'
      : `Ещё ${k} ${plural(k, TERMINALS)} не прислали ни одной отметки. Пройдите мимо каждого.`;
  return hint('bad', `Видим ${seen} ${plural(seen, TERMINALS)} из ${expected}.`, text);
}
const terminalBad =
  (code: TerminalProblemCode): CodeText =>
  ({ d }) => ({ summary: 'Нужно исправить терминалы', hints: [...terminalHints(code, d), ...nameTip(d)] });

// Шаг 9: пункты наблюдения (FR-026).
function observationDone(items: D): number {
  return [items.night, items.extract, items.terminals, items.clean].filter((x) => x === true).length;
}

/** Нет новых строк — это ещё не доказательство сбоя выгрузки. */
const extractUnconfirmed: CodeText = ({ d, site }) => {
  const next = str(d, 'next');
  // Одно утро без строк (уточнение 26.10.2026): когда появится «Подтвердить вручную», если и следующее окно пройдёт пусто.
  const manualFrom = str(d, 'manualFrom');
  const hints = [
    hint(
      'info',
      undefined,
      `После недавней полной выгрузки новых отметок может не быть. Без новых отметок нельзя определить, сработало ли расписание. Проверьте, что «Регулярно извлекать записи о посещаемости» включено и время указано.${
        next
          ? ` Ждём следующую выгрузку в ${localTime(next, site.tzOffset)}.`
          : ' Ждём следующую выгрузку по расписанию.'
      }${
        manualFrom
          ? ` Если и она ничего не добавит, ${localDate(manualFrom, site.tzOffset)} после ${localTime(manualFrom, site.tzOffset)} шаг можно будет подтвердить вручную.`
          : ''
      }`,
    ),
  ];
  // Два утра без строк выгрузки (уточнение 26.10.2026): кнопка «Подтвердить вручную» — в теле шага 7.
  if (d.manual === true) {
    hints.push(
      hint(
        'info',
        'Можно подтвердить вручную',
        'Выгрузка уже два утра ничего не добавила. Так бывает, когда все проходы доходят сразу: выгрузке нечего догружать. Если расписание в SmartPSS стоит, подтвердите шаг вручную. Если выгрузка потом что-то принесёт, сервис это увидит.',
      ),
    );
  }
  return { summary: 'Выгрузка пока не подтверждена', hints };
};

/** Тексты по каждому коду research R19. Ключи — все `StepCode`: новый код без текста не соберётся. */
export const CODE_TEXTS: Readonly<Record<StepCode, CodeText>> = {
  // Шаг 1 (FR-017). «Ключ защищённого соединения» не упоминается: ключ VPN выдаётся в шаге 3 (research R6).
  PREPARING: () => ({
    summary: 'Сервер готовит место для площадки…',
    hints: [hint('wait', undefined, 'Сервер готовит место для площадки: базу и вход для SmartPSS.')],
  }),
  READY: ({ step, d, site }) =>
    step === 1
      ? {
          summary: `${site.name} · ${cityLabel(site.city, site.tzOffset)}. Место на сервере готово`,
          hints: [hint('ok', undefined, 'Сервер подготовил место для площадки: базу и вход для SmartPSS.')],
        }
      : {
          summary: pcSummary(d),
          hints: [hint('ok', undefined, 'Всё в порядке. Версия проверена, срок хранения достаточный.')],
        },
  PREPARE_FAILED: ({ d }) => {
    const code = str(d, 'opsCode');
    return {
      summary: code ? `Сервер не подготовил место. Код ${code}` : 'Сервер не подготовил место',
      hints: [
        hint(
          'bad',
          'Сервер не подготовил место для площадки.',
          `Ничего ещё не подключено, данные не пострадали.${code ? ` Код ошибки ${code}.` : ''}`,
          [ACTIONS.retryPrepare()],
        ),
      ],
    };
  },

  // Шаг 2 (FR-018).
  ANSWERS_TODO: () => ({ summary: 'Ответьте на четыре вопроса' }),
  VERSION_UNKNOWN: pcWarn('VERSION_UNKNOWN'),
  RETENTION_SHORT: pcWarn('RETENTION_SHORT'),
  PC_NOT_24H: pcWarn('PC_NOT_24H'),

  // Шаг 3 (FR-019).
  LINK_DIRECT: () => ({
    summary: 'Напрямую через интернет. Ставить ничего не нужно',
    hints: [
      hint(
        'warn',
        'Без шифрования.',
        'ФИО и время проходов идут через интернет открытым текстом. Выбор записан в журнал действий.',
        [ACTIONS.gotoStep(4)],
      ),
    ],
  }),
  VPN_WAIT: () => ({
    summary: 'VPN: ждём сигнал от компьютера площадки…',
    hints: [
      hint(
        'wait',
        undefined,
        'Ждём сигнал от компьютера площадки. Обычно он приходит через несколько секунд после «Подключить». Ждём до 3 минут.',
      ),
    ],
  }),
  VPN_OK: ({ d }) => {
    const s = num(d, 'lastSignalSec');
    const ago = s === null ? null : `${formatDuration(s)} назад`;
    return {
      summary: ago ? `VPN: соединение есть, последний сигнал ${ago}` : 'VPN: соединение есть',
      hints: [
        hint('ok', undefined, ago ? `Соединение есть. Последний сигнал ${ago}.` : 'Соединение есть.', [
          ACTIONS.gotoStep(4),
        ]),
      ],
    };
  },
  VPN_SILENT: ({ site }) => ({
    summary: 'VPN: сигнала нет 3 минуты',
    hints: [
      hint(
        'bad',
        'Сигнала нет 3 минуты.',
        `Проверьте: в WireGuard у подключения «${tunnel(site)}» статус «Активен». Если нет — нажмите «Подключить».`,
        [ACTIONS.recheck(), ACTIONS.confirmVpnActive()],
      ),
    ],
  }),
  // Без своего сервера AmneziaWG — только «Подключить напрямую» (research R6).
  VPN_BLOCKED: ({ site }) => ({
    summary: 'VPN: сеть площадки закрывает соединение',
    hints: [
      site.amnezia
        ? hint(
            'bad',
            'Сеть площадки закрывает такое соединение.',
            'Для закрытых сетей есть другая программа — AmneziaWG. Удалите подключение из WireGuard, установите AmneziaWG и загрузите в неё новый файл.',
            [ACTIONS.openAmnezia(), ACTIONS.downloadAmneziaConfig(), ACTIONS.switchToDirect()],
          )
        : hint('bad', 'Сеть площадки закрывает такое соединение.', undefined, [ACTIONS.switchToDirect()]),
    ],
  }),

  // Шаг 3: исполнитель не сменил способ связи или ключ VPN (итог `set_link`, `new_vpn_key`; T034). «Повторить» —
  // та же заявка заново («Проверить снова»).
  LINK_FAILED: ({ d }) => {
    const code = str(d, 'opsCode');
    return {
      summary: code ? `Сервер не сменил способ связи. Код ${code}` : 'Сервер не сменил способ связи',
      hints: [
        hint(
          'bad',
          'Сервер не сменил способ связи.',
          `Новый способ связи ещё не работает.${code ? ` Код ошибки ${code}.` : ''}`,
          [ACTIONS.retryPrepare()],
        ),
      ],
    };
  },

  // Шаг 4 (FR-020, FR-021). `allowed` — адрес только что разрешили: отказы с него до разрешения не показываем.
  LOGIN_WAIT: ({ site, d }) =>
    str(d, 'allowed') !== null
      ? {
          summary: 'Адрес разрешён. Ждём, когда SmartPSS войдёт…',
          hints: [
            hint(
              'wait',
              `Адрес ${str(d, 'allowed')} разрешён.`,
              'Ждём, когда SmartPSS войдёт в базу с этого адреса. Обычно это несколько секунд. Нажимать «Разрешить» ещё раз не нужно.',
            ),
          ],
        }
      : d.legacyPending === true
        ? {
            // Первая площадка до выдачи пароля новой учётной записи (US5, R17).
            summary: 'Площадка работает под прежней учётной записью',
            hints: [
              hint(
                'info',
                undefined,
                'SmartPSS передаёт отметки под прежней учётной записью, отметки приходят. Чтобы перейти на новую учётную запись, нажмите «Выдать новый пароль» и введите значения в SmartPSS.',
              ),
            ],
          }
        : {
            summary: 'Ждём, когда SmartPSS войдёт в базу…',
            hints: [
              hint(
                'wait',
                undefined,
                `${vpn(site) ? 'Соединение с площадкой есть. ' : ''}Ждём, когда SmartPSS войдёт в базу. Нажмите «Тест соединения» в SmartPSS.`,
              ),
            ],
          },
  PASS_MISMATCH: () => ({
    summary: 'Пароль не подошёл',
    hints: [
      hint(
        'bad',
        'SmartPSS пришёл, но пароль не подошёл.',
        'Скопируйте пароль кнопкой и вставьте в SmartPSS заново. Потом снова нажмите «Тест соединения».',
        [ACTIONS.copyPassword(), ACTIONS.recheck()],
      ),
    ],
  }),
  USER_NAME: ({ site }) => ({
    summary: 'Неверное имя пользователя',
    hints: [
      hint('bad', 'Неверное имя пользователя.', `Нужно ${site.smartpssUser}.`, [
        ACTIONS.copyUser(site.smartpssUser),
        ACTIONS.recheck(),
      ]),
    ],
  }),
  DB_NAME: ({ site }) => ({
    summary: 'Неверное имя базы',
    hints: [
      hint('bad', 'Неверное имя базы.', `Нужно ${site.dbName}.`, [
        ACTIONS.copyDbName(site.dbName),
        ACTIONS.recheck(),
      ]),
    ],
  }),
  NO_ATTEMPTS: ({ site }) => ({
    summary: 'SmartPSS не обращается к сервису',
    hints: [
      hint(
        'bad',
        'SmartPSS не обращается к сервису.',
        [
          vpn(site) ? 'Соединение с площадкой есть, но SmartPSS не пробовал войти.' : null,
          `Проверьте IP сервера ${site.serverIp} и порт ${port(site)}.`,
          'Нажмите «Тест соединения» ещё раз.',
          vpn(site)
            ? null
            : `Если не помогло, сеть площадки может закрывать порт ${port(site)} — тогда выберите VPN в шаге 3.`,
        ]
          .filter(Boolean)
          .join(' '),
        [ACTIONS.recheck()],
      ),
    ],
  }),
  NEW_ADDRESS: ({ d }) => {
    const from = str(d, 'from') ?? '';
    const allowed = str(d, 'allowed') ?? '';
    return {
      summary: 'SmartPSS пришёл с нового адреса',
      hints: [
        hint(
          'bad',
          `SmartPSS пришёл с адреса ${from}, а площадке разрешён ${allowed}.`,
          'Так бывает, когда у площадки сменился интернет. Если это ваша площадка — разрешите новый адрес.',
          from ? [ACTIONS.allowAddress(from)] : [],
        ),
      ],
    };
  },
  LOGGED_IN: () => ({
    summary: 'SmartPSS вошёл. Ждём, когда он создаст таблицу…',
    hints: [
      hint('ok', undefined, 'SmartPSS вошёл в базу.'),
      hint(
        'wait',
        undefined,
        'Теперь включите «Включить базу данных» и сохраните. Ждём, когда SmartPSS создаст таблицу.',
      ),
    ],
  }),
  NO_TABLE: () => ({
    summary: 'SmartPSS вошёл, но таблица не появилась',
    hints: [
      hint('ok', undefined, 'SmartPSS вошёл в базу.'),
      hint(
        'bad',
        'Таблица не появилась за 3 минуты.',
        'Включите «Включить базу данных» и нажмите «Сохранить».',
        [ACTIONS.recheck()],
      ),
    ],
  }),
  // `idle` — связь уже работала, а сейчас SmartPSS не подключён: он входит, только когда есть новая отметка (ночью
  // MySQL закрывает соединение). Это не сбой; долгую тишину в рабочее время сообщает тревога.
  LINK_OK: ({ d, site }) => {
    const address = str(d, 'address');
    if (d.idle === true) {
      const last = str(d, 'lastRowAt');
      const when = last ? `${localDate(last, site.tzOffset)} в ${localTime(last, site.tzOffset)}` : null;
      return {
        summary: when ? `Связь настроена. Последняя отметка пришла ${when}` : 'Связь настроена',
        hints: [
          hint(
            'ok',
            'Связь настроена.',
            `SmartPSS подключается к сервису, только когда есть новая отметка.${
              when ? ` Последняя отметка пришла ${when}.` : ''
            }`,
            [ACTIONS.gotoStep(5)],
          ),
        ],
      };
    }
    return {
      summary: 'Связь работает. SmartPSS передаёт отметки',
      hints: [
        hint(
          'ok',
          'Связь работает.',
          `SmartPSS создал таблицу, копия отметок подключена.${
            !vpn(site) && address ? ` Адрес площадки ${address} запомнен: вход с других адресов закрыт.` : ''
          }`,
          [ACTIONS.gotoStep(5)],
        ),
      ],
    };
  },

  // Шаг 5 (FR-022).
  TERMINALS_WAIT: () => ({
    summary: 'Ждём первые отметки с терминалов…',
    hints: [
      hint(
        'wait',
        undefined,
        'Сервис найдёт терминалы по первым отметкам. Попросите кого-нибудь пройти мимо каждого терминала.',
      ),
    ],
  }),
  TERMINALS_CHOOSE: ({ d }) => ({
    summary: 'Выберите для каждого терминала, вход это или выход',
    hints: nameTip(d),
  }),
  TERMINALS_OK: ({ d }) => {
    const ts = objs(d, 'terminals');
    const ips = (dir: string) => ts.filter((t) => t.chosen === dir).map((t) => str(t, 'ip') ?? '');
    const parts = [
      ips('IN').length ? `вход ${ips('IN').join(', ')}` : null,
      ips('OUT').length ? `выход ${ips('OUT').join(', ')}` : null,
    ].filter((x): x is string => !!x);
    const summary = parts.length ? parts.join(', ') : 'терминалы настроены верно';
    return {
      summary: summary.charAt(0).toUpperCase() + summary.slice(1),
      hints: [hint('ok', undefined, 'Терминалы настроены верно.', [ACTIONS.gotoStep(6)]), ...nameTip(d)],
    };
  },
  TERMINALS_MISSING: ({ d }) => ({
    summary: 'Нужно исправить терминалы',
    hints: [...terminalHints('TERMINALS_MISSING', d), missingTerminals(d), ...nameTip(d)],
  }),
  NO_DIRECTION: terminalBad('NO_DIRECTION'),
  DIRECTION_SWAPPED: terminalBad('DIRECTION_SWAPPED'),
  CLOCK_SKEW: terminalBad('CLOCK_SKEW'),

  // Шаг 6 (FR-023).
  CTL_WAIT: ({ status, d }) =>
    status === 'wait'
      ? {
          summary: 'Ищем отметку рядом с нажатием…',
          hints: [hint('wait', undefined, 'Сервис ищет отметку рядом с нажатием: за 2 минуты до и после.')],
        }
      : {
          summary: 'Человек проходит вход и выход',
          hints:
            objs(d, 'found').length === 0
              ? [
                  hint(
                    'info',
                    undefined,
                    'Сервис будет искать отметку рядом с нажатием: за 2 минуты до и после.',
                  ),
                ]
              : [],
        },
  CTL_OK: () => ({
    summary: 'Обе отметки пришли вовремя',
    hints: [hint('ok', undefined, 'Обе отметки пришли вовремя, с нужных терминалов.', [ACTIONS.gotoStep(7)])],
  }),
  CTL_SKIPPED: () => ({
    summary: 'Проверка пропущена',
    hints: [
      hint(
        'info',
        undefined,
        'Контрольный проход не проверен. Можно продолжить подключение и вернуться к проверке позже.',
        [ACTIONS.gotoStep(7)],
      ),
    ],
  }),
  CTL_NOT_FOUND: () => ({
    summary: 'Отметка не пришла',
    hints: [
      hint(
        'bad',
        'Отметка не пришла.',
        'Проверьте, что терминал узнал человека: на экране терминала должно появиться его имя. Пройдите ещё раз.',
        [ACTIONS.restartControl()],
      ),
    ],
  }),
  CTL_WRONG_TERMINAL: ({ d }) => {
    const dir = str(d, 'direction');
    const from =
      dir === 'OUT' ? 'с терминала выхода' : dir === 'IN' ? 'с терминала входа' : 'с другого терминала';
    return {
      summary: 'Отметка пришла не с того терминала',
      hints: [
        hint('bad', `Отметка пришла ${from}.`, 'Проверьте направления в шаге 5.', [ACTIONS.gotoStep(5)]),
      ],
    };
  },
  CTL_CLOCK: ({ d }) => {
    const m = Math.max(1, Math.abs(num(d, 'minutes') ?? 1));
    return {
      summary: 'Время отметки расходится с нажатием',
      hints: [
        hint(
          'bad',
          `Часы терминала расходятся с сервером на ${m} ${plural(m, MINUTES_ACC)}.`,
          'Включите синхронизацию времени в настройках устройства в SmartPSS.',
          [ACTIONS.gotoStep(5)],
        ),
      ],
    };
  },
  CTL_DELAY: () => ({
    summary: 'Отметки приходят с задержкой',
    hints: [
      hint(
        'warn',
        'Отметки приходят с задержкой.',
        'Табель это не портит, но тревоги о тишине будут позже.',
        [ACTIONS.acceptRisk()],
      ),
    ],
  }),

  // Шаг 7 (FR-024).
  EXTRACT_TODO: () => ({ summary: 'Можно сделать в любой момент' }),
  EXTRACT_WAIT: ({ d, site }) => {
    if (d.unconfirmed === true) return extractUnconfirmed({ step: 7, status: 'wait', d, site });
    const at = str(d, 'at');
    const t = at ? localTime(at, site.tzOffset) : null;
    return {
      summary: t ? `Ждём выгрузку в ${t}` : 'Ждём выгрузку по расписанию',
      hints: [
        hint(
          'wait',
          undefined,
          t
            ? `Сервис ждёт первую выгрузку по расписанию — в ${t}. Галочка появится сама.`
            : 'Сервис ждёт первую выгрузку по расписанию. Галочка появится сама.',
        ),
      ],
    };
  },
  // Время первой выгрузки — с датой: через день голое «в 13:00» читается как «сегодня в 13:00».
  EXTRACT_OK: ({ d, site }) => {
    const at = str(d, 'at');
    const t = at ? `${localDate(at, site.tzOffset)} в ${localTime(at, site.tzOffset)}` : null;
    const rows = num(d, 'rows');
    const added = num(d, 'added');
    const head = t ? `Выгрузка ${t} пришла` : 'Выгрузка пришла';
    const tail =
      rows === null
        ? ''
        : `: ${rows} ${plural(rows, PASSES)}${added === null ? '' : `, из них новых ${added}`}`;
    return { summary: `${head}${tail}`, hints: [hint('ok', undefined, `${head}${tail}.`)] };
  },
  EXTRACT_MANUAL: ({ d, site }) => {
    const at = str(d, 'at');
    const t = at ? `${localDate(at, site.tzOffset)} в ${localTime(at, site.tzOffset)}` : null;
    const head = t ? `Подтверждено вручную ${t}` : 'Подтверждено вручную';
    return {
      summary: head,
      hints: [
        hint(
          'ok',
          undefined,
          `${head}: расписание в SmartPSS стоит, выгрузке нечего было добавить. Когда выгрузка что-то принесёт, шаг покажет её.`,
        ),
      ],
    };
  },
  EXTRACT_MISSED: extractUnconfirmed,

  // Шаг 8 (FR-025): только число людей и время загрузки; похожих пар в 007 нет.
  PEOPLE_NONE: () => ({ summary: 'По желанию. Лучше до запуска' }),
  PEOPLE_OK: ({ d, site }) => {
    const n = num(d, 'count') ?? 0;
    const at = str(d, 'at');
    const head = `Загружено ${n} ${plural(n, PEOPLE)}`;
    return {
      summary: head,
      hints: [
        hint(
          'ok',
          undefined,
          `${head}.${at ? ` Файл загружен ${localDate(at, site.tzOffset)} в ${localTime(at, site.tzOffset)}.` : ''}`,
        ),
      ],
    };
  },

  // Шаг 9 (FR-026).
  OBSERVING: ({ d }) => {
    const items = isObj(d.items) ? d.items : {};
    const days = num(items, 'days') ?? 0;
    const k = observationDone(items);
    return { summary: `Идёт: день ${Math.min(3, days + 1)} из 3, готово ${k} ${plural(k, ITEMS)} из 4` };
  },
  OBSERVED: ({ d }) => {
    const items = isObj(d.items) ? d.items : {};
    const days = Math.max(3, num(items, 'days') ?? 3);
    return {
      summary: `Все пункты пройдены за ${days} ${plural(days, DAYS)}`,
      hints: [hint('ok', undefined, 'Наблюдение пройдено.', [ACTIONS.gotoStep(10)])],
    };
  },
  OBS_DELETES: () => ({
    summary: 'В SmartPSS удалили записи',
    hints: [
      hint(
        'warn',
        'В SmartPSS удалили записи.',
        'В сервисе отметки остались. Проверьте, кто удаляет записи в SmartPSS.',
        [ACTIONS.acceptRisk()],
      ),
    ],
  }),
  OBS_CODES: () => ({
    summary: 'Пришли отметки с незнакомыми кодами',
    hints: [
      hint(
        'warn',
        'Пришли отметки с незнакомыми кодами.',
        'Сервис сохранил эти отметки, но в табель они не идут. Проверьте, не меняли ли настройки SmartPSS.',
        [ACTIONS.acceptRisk()],
      ),
    ],
  }),

  // Шаг 10 (FR-027). «Пользователи и роли» в сервисе пока нет (FR-001): подсказка без ссылки на раздел.
  LAUNCH_READY: () => ({ summary: 'Всё готово к запуску' }),
  LAUNCHED: ({ site }) => ({
    summary: site.launchedAt
      ? `Площадка подключена к табелям ${localDate(site.launchedAt, site.tzOffset)}`
      : 'Площадка подключена к табелям',
    hints: [
      hint(
        'ok',
        'Площадка подключена к табелям.',
        'Тревоги включены. Назначьте бригадиров и кадры площадки.',
      ),
    ],
  }),

  // Закрытый шаг (FR-011): читается, кнопки недоступны, причина написана.
  LOCKED: ({ step, d, site }) => {
    const listed = nums(d, 'missing');
    const missing = listed.length ? listed : [...(STEP_AFTER[step as StepNo] ?? [])];
    const summary = `Откроется после ${afterSteps(missing)}`;
    if (step === 10) {
      const others = missing.filter((n) => n !== 9);
      // «шагов 2, 4–7 и наблюдения»: одно «и» на весь список.
      const parts = stepParts(others);
      const what = !others.length
        ? 'наблюдения'
        : missing.includes(9)
          ? `${parts.length === 1 && !parts[0]?.includes('–') ? 'шага' : 'шагов'} ${joinAnd([...parts, 'наблюдения'])}`
          : afterSteps(others);
      const done = site.doneCount === undefined ? '' : ` Сейчас готово ${site.doneCount} из 10.`;
      return { summary, note: `Станет доступно после ${what}.${done}` };
    }
    const ready =
      missing.length === 1 ? `будет готов шаг ${missing[0]}` : `будут готовы шаги ${stepsPhrase(missing)}`;
    return { summary, note: `Шаг откроется, когда ${ready}. Прочитать его можно уже сейчас.` };
  },
};

// ── Постоянные тексты шагов ──

/** Тексты тела шагов: поля, «Что сделать», подписи таблиц и кнопок. */
export const STEP_BODY = {
  1: {
    nameLabel: 'Название',
    cityLabel: 'Часовой пояс',
    otherCity: 'Другой город',
    otherCityLabel: 'Город с таким же временем',
    tzLabel: 'Часовой пояс вручную',
  },
  2: {
    note: 'Ответы — со слов администратора. Сервис проверит их на деле, где сможет.',
    version: { label: 'Версия SmartPSS (необязательно)', hint: 'SmartPSS → значок «?» → «О программе»' },
    terminals: { label: 'Сколько терминалов на площадке', hint: 'Входа и выхода вместе' },
    allDay: { label: 'Компьютер работает круглосуточно?', yes: 'Да', no: 'Нет' },
    keepDays: {
      label: 'Срок хранения данных, дней',
      hint: '«Системные настройки» → «Управление данными» → «Данные очищены»',
    },
  },
  3: {
    note: 'Выберите, как SmartPSS будет передавать отметки. Способ можно сменить позже.',
    modes: {
      direct: {
        title: 'Напрямую через интернет',
        need: 'Ставить ничего не нужно. SmartPSS передаёт отметки на адрес сервиса.',
        get: 'Отметки идут без шифрования. Если у площадки сменится адрес в интернете, сервис спросит, разрешить ли новый адрес.',
      },
      vpn: {
        title: 'Через защищённое соединение (VPN)',
        need: 'Нужна программа WireGuard на компьютере площадки, 10 минут.',
        get: 'Отметки идут в шифрованном виде. Смена адреса площадки ничего не ломает. Сервис видит, включён ли компьютер, даже когда отметок нет.',
      },
    },
    /** Первый пункт — со ссылкой `LINKS.wireguard`. */
    todo: [
      'Скачайте и установите программу WireGuard для Windows:',
      'Скачайте файл подключения этой площадки.',
      'В WireGuard нажмите «Импорт туннеля из файла» и выберите этот файл.',
      'Нажмите «Подключить».',
    ],
    download: 'Скачать файл подключения',
    reissue: 'Выдать новый файл',
    caption:
      'Файл можно скачать, пока шаг не выполнен. Потом — только «Выдать новый файл»: старый перестанет работать.',
  },
  4: {
    todo: [
      'В SmartPSS откройте «Настройки» → «База данных посещаемости (сторонняя)».',
      'Заполните поля значениями ниже. Копируйте каждое кнопкой, не набирайте руками.',
      'Нажмите «Тест соединения».',
      'Включите «Включить базу данных» и сохраните.',
    ],
    values: {
      dbType: 'Тип базы',
      serverIp: 'IP сервера',
      port: 'Порт',
      dbName: 'Имя базы',
      user: 'Имя пользователя',
      password: 'Пароль',
    },
    show: 'Показать',
    hide: 'Скрыть',
    showPassword: 'Показать пароль',
    hidePassword: 'Скрыть пароль',
    schemeTitle: 'Окно SmartPSS — где какое поле (схема)',
    testButton: 'Тест соединения',
    enableButton: 'Включить базу данных',
    reissue: 'Выдать новый пароль',
    caption:
      'Пароль виден до конца шага. Потом — только «Выдать новый пароль»: его нужно будет ввести в SmartPSS заново.',
  },
  5: {
    todo: [
      'На терминале входа включите режим «Регистрация входа». На терминале выхода — «Выйти».',
      'Попросите кого-нибудь пройти мимо каждого терминала.',
      'Выберите для каждого терминала, вход это или выход.',
    ],
    caption: 'Сервис нашёл терминалы по первым отметкам. Адреса вводить не нужно.',
    columns: {
      terminal: 'Терминал',
      count: 'Отметок',
      last: 'Последняя',
      direction: 'Направление',
      check: 'Проверка',
    },
    deviceName: 'Имя в SmartPSS:',
    directions: { IN: 'Вход', OUT: 'Выход', SKIP: 'Не учитывать' },
    check: { ok: 'Верно', skip: 'Не учитывается', bad: 'Исправить' },
    manualDirections: {
      note: 'Сервис будет определять вход и выход по выбранным направлениям терминалов. Проверьте выбор и подтвердите его.',
      confirm: 'Подтвердить направления',
      confirmed: 'Направления подтверждены. Сервис определяет вход и выход по вашему выбору.',
    },
    nameTip: {
      title: 'Совет.',
      text: 'Назовите терминалы в SmartPSS «Вход» и «Выход». Сейчас их имена равны адресам.',
    },
  },
  6: {
    todo: [
      'Откройте этот шаг на телефоне — у человека, который будет проходить.',
      'Человек проходит вход и в этот момент нажимает «Прошёл вход».',
      'Потом проходит выход и нажимает «Прошёл выход».',
    ],
    copyLink: 'Скопировать ссылку для телефона',
    passedIn: 'Прошёл вход',
    passedOut: 'Прошёл выход',
    outAfterIn: '«Прошёл выход» станет доступно после входа.',
    optionalNote:
      'Проверка необязательна. Если сейчас некому пройти, пропустите её и продолжите подключение. Проверить вход и выход можно позже.',
    skip: 'Пропустить проверку',
    columns: {
      pressed: 'Нажали',
      pass: 'Отметка СКУД',
      terminal: 'Терминал',
      delay: 'Задержка',
      result: 'Итог',
    },
    results: { ok: 'Верно', lost: 'Не пришла', wrong: 'Не тот терминал' },
    notFound: 'нет за 2 мин',
  },
  7: {
    place: 'В SmartPSS откройте «Системные настройки» → «Управление данными».',
    /** Времена выгрузки — из ответа шага; по умолчанию одно, 08:00 (FR-024). */
    todo: (times: readonly string[]): string[] => [
      `«Регулярно извлекать записи о посещаемости» — включите. Время: ${times.join(', ')}.`,
      '«Автоматически извлекать записи о посещаемости» — включите.',
      '«Данные очищены» — 62 дня или больше.',
    ],
    defaultTimes: ['08:00'],
    done: 'Готово',
    /** Два утра без строк выгрузки (уточнение 26.10.2026). */
    confirm: 'Подтвердить вручную',
  },
  8: {
    todo: ['В SmartPSS откройте «Персонал» → «Управление персоналом» → «Экспорт».', 'Загрузите файл сюда.'],
    upload: 'Загрузить файл',
    uploadHint: 'Excel или CSV из SmartPSS',
  },
  10: {
    startLabel: 'Начать табели с',
    startHint: 'Первый день табелей площадки. Направления терминалов из шага 5 действуют с этого дня.',
    rulesLabel: 'Правила как у площадки',
    rulesHint:
      'Сервис копирует текущие правила этой площадки. Отличия этой площадки можно внести до запуска: нажмите «Подготовить правила».',
    historyLabel: 'Отметки за прошлые дни — по желанию',
    historyUpload: 'Загрузить выгрузку',
    historyHint: 'SmartPSS: «Посещаемость» → «Записи» → «Экспорт» за период',
    launch: 'Подключить к табелям',
    // Поправка 20.10.2026 (FR-004a, FR-027): правила к запуску и кто ведёт правила площадки. Итог — подсказкой
    // Callout в одну-две строки (дизайн-система Steps, Callout); часть шага, доступная всегда, — «Можно сделать в любой
    // момент» (Steps).
    adminsLabel: 'Кто ведёт правила площадки',
    adminsHint: 'Получат роль «Администратор правил» на площадке. Снять её здесь нельзя.',
    adminsCurrent: 'уже ведёт',
    adminsSource: 'ведёт правила образца',
    adminsEmpty: 'Выберите хотя бы одного человека.',
    adminsError: 'Список людей не загрузился',
    prepare: 'Подготовить правила',
    prepareHint: 'Можно сделать в любой момент. Правила начнут действовать при запуске.',
    preparedToast: 'Правила к запуску подготовлены',
    preparedTitle: 'Правила к запуску готовы.',
    prepared: (source: string, changes: number) =>
      changes === 0
        ? `Копия правил «${source}», отличий от образца нет.`
        : `Копия правил «${source}», отличий от образца: ${changes}.`,
    sourceChanged: (source: string) =>
      `Правила «${source}» изменились после начала черновика. В запуск войдут правила черновика.`,
    sourceLocked:
      'Образец сменить нельзя: правила к запуску уже меняли. Чтобы сменить образец, отмените черновик на странице правил.',
    openRules: 'Открыть правила',
  },
} as const;

/** Подсказка шага 1 под городом: «Пояс +05:00. 08:00 на площадке = 08:00 по Екатеринбургу». */
export function tzHint(tz: string): string {
  return `Пояс ${tz}. ${tzExample(tz)}`;
}

/** Шаг 9: пункты и итог дня (FR-026). */
export const OBSERVATION = {
  note: 'Ничего делать не нужно. Сервис отмечает пункты сам. Наблюдение — не меньше 3 дней.',
  daysTitle: 'Итоги дня',
  items: {
    night: { title: 'Ночь: соединение пережило ночь, утром отметки пошли', pending: 'Сколько ждать: 1 ночь' },
    extract: { title: 'Выгрузка по расписанию пришла', pending: 'Сколько ждать: 1 выгрузка' },
    terminals: { title: 'Рабочий день: отметки на всех терминалах', pending: 'Сколько ждать: 1 день' },
    clean: { title: 'Удалений и незнакомых кодов нет' },
  },
  firstPass: (date: string, time: string) => `Первая отметка ${date} в ${time}`,
  extractCame: (date: string, time: string, passes: number) =>
    `${date} в ${time}: ${passes} ${plural(passes, PASSES)}`,
  /** Шаг 7 подтверждён вручную, выгрузка ещё ничего не принесла (уточнение 26.10.2026). */
  extractManual: (date: string) => `Подтверждено вручную на шаге 7, ${date}`,
  terminalsSeen: (seen: number, total: number) => `${seen} ${plural(seen, TERMINALS)} из ${total}`,
  cleanDays: (days: number) => `${days} ${plural(days, DAYS)}`,
  cleanLeft: (left: number) =>
    `${plural(left, ['Остался', 'Осталось', 'Осталось'])} ${left} ${plural(left, DAYS)}`,
  /** Итог дня одной строкой: «20.10: 388 отметок, ночью тишина 22:40–05:50, выгрузка 08:00 пришла». */
  day: (x: {
    date: string;
    passes: number;
    quiet?: readonly [string, string] | null;
    extracts: readonly { at: string; arrived: boolean }[];
  }): string => {
    const parts = [`${x.date}: ${x.passes} ${plural(x.passes, PASSES)}`];
    if (x.quiet) parts.push(`ночью тишина ${x.quiet[0]}–${x.quiet[1]}`);
    for (const arrived of [true, false]) {
      const ats = x.extracts.filter((e) => e.arrived === arrived).map((e) => e.at);
      if (ats.length === 0) continue;
      const one = ats.length === 1;
      parts.push(
        `${one ? 'выгрузка' : 'выгрузки'} ${joinAnd(ats)} ${arrived ? '' : 'не '}${one ? 'пришла' : 'пришли'}`,
      );
    }
    return parts.join(', ');
  },
} as const;

/** Напоминание о шаге 8 перед запуском — один раз (FR-027). */
export function launchReminder(): StepHint {
  return hint('info', 'Список людей площадки не загружен.', 'Его можно загрузить и после запуска.', [
    ACTIONS.gotoStep(8),
  ]);
}

/** «Продолжить с риском» (FR-015): окно с причиной, причина — в журнал действий. */
export const RISK_TEXTS = {
  action: 'Продолжить с риском',
  title: 'Продолжить с риском',
  reasonLabel: 'Причина',
  reasonHint: 'От 5 до 500 знаков. Причина попадёт в журнал действий.',
  submit: 'Продолжить',
  toast: 'Риск записан в журнал действий',
  accepted: (reason: string) => `Риск принят. Причина: ${reason}`,
} as const;

/** Общие тексты страницы площадки. */
export const COMMON = {
  todoTitle: 'Что сделать',
  checkTitle: 'Проверка',
  copy: 'Скопировать',
  copied: 'Скопировано',
  recheckStarted: 'Проверка запущена',
  support: 'Сведения для поддержки',
  optional: 'по желанию',
  progress: (done: number) => `Готово ${done} из 10`,
} as const;

/** Список площадок: способ связи, пометка, состояния (FR-002, `site-onboarding-design.md` раздел 2). */
export const SITE_TEXTS = {
  linkMode: { direct: 'Напрямую через интернет', vpn: 'Через VPN' },
  unencrypted: 'без шифрования',
} as const;

export type BadgeTone = 'accent' | 'ok' | 'bad' | 'neutral';

/** Бейдж состояния площадки: «Подключение · шаг 4 из 10», «Наблюдение · день 2 из 3», «Работает»… */
export function siteStateBadge(x: {
  state: SiteState;
  currentStep: number | null;
  observationDay: number | null;
}): { text: string; tone: BadgeTone } {
  switch (x.state) {
    case 'onboarding':
      return { text: `Подключение · шаг ${x.currentStep ?? 1} из 10`, tone: 'accent' };
    case 'observing':
      return { text: `Наблюдение · день ${Math.min(3, x.observationDay ?? 1)} из 3`, tone: 'accent' };
    case 'active':
      return { text: 'Работает', tone: 'ok' };
    case 'no_link':
      return { text: 'Нет связи', tone: 'bad' };
    case 'disabled':
      return { text: 'Отключена', tone: 'neutral' };
  }
}
